import moment from 'moment';
import { CyclabilityZone, CyclabilityZoneBNSCStats, CyclabilityZoneStats, Facilities, ParkingFurnitureTypes, ParkingTypes } from '../models';
import { HttpService } from './http';
function getPeriodParams(period) {
  if (!period) return [];
  if (period === 'all_time') return [{
    key: 'period',
    value: 'all_time'
  }];
  return [{
    key: 'period',
    value: 'custom'
  }, {
    key: 'date_start',
    value: period.from.format('DD-MM-YYYY')
  }, {
    key: 'date_end',
    value: period.to.format('DD-MM-YYYY')
  }];
}
export class CyclabilityZoneService {
  static async getZones({
    ids,
    countryCode,
    name,
    ref,
    partnerCode,
    point,
    period,
    query,
    search,
    page,
    rowsPerPage,
    getAllItems = true,
    ...otherProps
  }) {
    const queryParams = [{
      key: 'administrative_level',
      value: 'administrativeLevel' in otherProps ? otherProps.administrativeLevel : otherProps.administrativeLevels.join(',')
    }];
    if (countryCode) queryParams.push({
      key: 'country_code',
      value: countryCode
    });
    if (name) queryParams.push({
      key: 'name',
      value: name
    });
    if (ref) queryParams.push({
      key: 'reference',
      value: ref
    });
    if (partnerCode) queryParams.push({
      key: 'partner_code',
      value: partnerCode
    });
    if (point) queryParams.push({
      key: 'longitude',
      value: point.coordinates[0]
    }, {
      key: 'latitude',
      value: point.coordinates[1]
    });
    queryParams.push(...getPeriodParams(period));
    if (query) queryParams.push({
      key: 'query',
      value: query
    });
    if (search) queryParams.push({
      key: 'search',
      value: search
    });
    if (rowsPerPage) queryParams.push({
      key: 'page_size',
      value: rowsPerPage
    });
    if (ids) {
      ids.forEach(id => queryParams.push({
        key: 'ids[]',
        value: id
      }));
    }
    try {
      const {
        count,
        results
      } = await HttpService.get('v4', '/cyclability_zones', [...queryParams, {
        key: 'page',
        value: page || 1
      }]);
      const cyclabilityZones = results.reduce((res, data) => {
        const cyclabilityZone = parseCyclabilityZone(data);
        if (cyclabilityZone) res.push(cyclabilityZone);
        return res;
      }, []);
      if (rowsPerPage && count > rowsPerPage && getAllItems) {
        const res = await Promise.all(new Array(Math.ceil((count - rowsPerPage) / rowsPerPage)).fill(null).map((_, index) => HttpService.get('v4', '/cyclability_zones', [...queryParams, {
          key: 'page',
          value: index + 2
        }])));
        cyclabilityZones.push(...res.flatMap(({
          results: pageResults
        }) => pageResults.reduce((res, data) => {
          const cyclabilityZone = parseCyclabilityZone(data);
          if (cyclabilityZone) res.push(cyclabilityZone);
          return res;
        }, [])));
      }
      return {
        count,
        zones: cyclabilityZones
      };
    } catch (err) {
      console.error('[CyclabilityZoneService][getZones]', err);
      throw err;
    }
  }
  static async getZone(id, {
    period,
    query
  }) {
    const queryParams = [];
    queryParams.push(...getPeriodParams(period));
    if (query) queryParams.push({
      key: 'query',
      value: query
    });
    try {
      const data = await HttpService.get('v4', `/cyclability_zones/${id}`, queryParams);
      const zone = parseCyclabilityZone(data);
      if (!zone) throw new Error('zone not found');
      return zone;
    } catch (err) {
      console.error('[CyclabilityZoneService][getZone]', err);
      throw err;
    }
  }
  static async getParentZone(zone, {
    period,
    query
  } = {}) {
    try {
      const queryParams = getPeriodParams(period);
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const data = await HttpService.get('v4', `/cyclability_zones/${zone.id}/parents`, queryParams);
      if ('detail' in data) return null;
      const parentAdministrativeLevel = backendParentAdministrativeLevels[zone.administrativeLevel];
      const parentData = data.results.find(({
        administrative_level
      }) => administrative_level === parentAdministrativeLevel);
      const parentZone = parentData ? parseCyclabilityZone(parentData) : null;
      return parentZone;
    } catch (err) {
      console.error('[CyclabilityZoneService][getParentZone]', err);
      return null;
    }
  }
  static async getChildrenZones(parentZoneId, {
    period,
    query,
    ...otherProps
  }) {
    try {
      const queryParams = [{
        key: 'administrative_level',
        value: 'administrativeLevel' in otherProps ? otherProps.administrativeLevel : ''
      }, {
        key: 'page_size',
        value: 100
      }];
      queryParams.push(...getPeriodParams(period));
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const {
        count,
        results
      } = await HttpService.get('v4', `/cyclability_zones/${parentZoneId}/children`, [...queryParams, {
        key: 'page',
        value: 1
      }]);
      const zones = results.reduce((res, data) => {
        const cyclabilityZone = parseCyclabilityZone(data);
        if (cyclabilityZone) res.push(cyclabilityZone);
        return res;
      }, []);
      if (count > 100) {
        const res = await Promise.all(new Array(Math.ceil((count - 100) / 100)).fill(null).map((_, index) => HttpService.get('v4', `/cyclability_zones/${parentZoneId}/children`, [...queryParams, {
          key: 'page',
          value: index + 2
        }])));
        zones.push(...res.flatMap(({
          results: pageResults
        }) => pageResults.reduce((res, data) => {
          const cyclabilityZone = parseCyclabilityZone(data);
          if (cyclabilityZone) res.push(cyclabilityZone);
          return res;
        }, [])));
      }
      return zones;
    } catch (err) {
      console.error('[CyclabilityZoneService][getChildrenZones]', err);
      throw err;
    }
  }
  static async getEventChallenges(zone, eventId) {
    try {
      const data = await HttpService.get('v2', `/cyclability_zones/${zone.id}/challenges`, [{
        key: 'event_id',
        value: eventId
      }]);
      return data.map(({
        challenge_id: challengeId,
        challenge_title: challengeTitle,
        group_id: groupId,
        group_title: groupTitle
      }) => ({
        challengeId,
        challengeTitle,
        groupId,
        groupTitle
      }));
    } catch (err) {
      console.error('[CyclabilityZoneService][getEventChallenges]', err);
      throw err;
    }
  }
  static async getPartnerContracts(zone) {
    try {
      const data = await HttpService.get('v2', `/cyclability_zones/${zone.id}/partner_contracts`);
      return data.map(({
        partner_id: partnerId
      }) => ({
        partnerId
      }));
    } catch (err) {
      console.error('[CyclabilityZoneService][getPartnerContracts]', err);
      throw err;
    }
  }
  static async getTerritories(zone) {
    try {
      const data = await HttpService.get('v2', `/cyclability_zones/${zone.id}/groups`);
      return data.map(({
        group_id: id,
        group_title: title
      }) => ({
        id,
        title
      }));
    } catch (err) {
      console.error('[CyclabilityZoneService][getTerritories]', err);
      throw err;
    }
  }
}
export const administrativeLevels = {
  DISTRICT: 'district',
  CITY: 'city',
  COUNTRY: 'country',
  DEPARTMENT: 'department',
  EPCI: 'epci',
  REGION: 'region'
};
export const backendAdministrativeLevels = {
  district: 'DISTRICT',
  city: 'CITY',
  country: 'COUNTRY',
  department: 'DEPARTMENT',
  epci: 'EPCI',
  region: 'REGION'
};
export const backendParentAdministrativeLevels = {
  district: null,
  city: 'EPCI',
  epci: 'DEPARTMENT',
  department: 'REGION',
  region: 'COUNTRY',
  country: null,
  world: null
};
function parseCyclabilityZone({
  id,
  administrative_level,
  reference,
  country_code,
  name,
  geo_polygon: geometry,
  geo_polygon_simplified: geometrySimplified,
  stats
}) {
  function parse(prop) {
    return prop || 0;
  }
  if (!administrative_level) throw new Error('no administrative_level');
  const distancesList = [];
  const slotsList = [];
  stats?.sort((a, b) => a.created.localeCompare(b.created)).map(({
    created,
    bnac_data: {
      mixedfacilities,
      lane,
      sharebusway,
      opposite,
      cycleway,
      greenway
    },
    bnsc_data: {
      gratuite: {
        gratuit,
        payant
      },
      capacite,
      couverture: {
        couvert
      },
      mobilier: {
        arceau,
        arceau_velo_grande_taille,
        aucun_equipement,
        autre,
        crochet,
        potelet,
        rack_double_etage,
        ratelier,
        support_guidon
      },
      protection: {
        box_individuel_ferme,
        consigne_collective_fermee
      }
    }
  }) => {
    const distances = {
      [Facilities.Cycleways]: parse(cycleway),
      [Facilities.Greenways]: parse(greenway),
      [Facilities.Lanes]: parse(lane),
      [Facilities.Opposites]: parse(opposite),
      [Facilities.SharedBusways]: parse(sharebusway),
      [Facilities.MixedFacilities]: parse(mixedfacilities)
    };
    const slots = {
      [ParkingTypes.Free]: parse(gratuit),
      [ParkingTypes.Private]: parse(payant),
      [ParkingTypes.Sheltered]: parse(couvert),
      [ParkingTypes.Secure]: parse(box_individuel_ferme) + parse(consigne_collective_fermee),
      [ParkingTypes.Arch]: parse(arceau) + parse(arceau_velo_grande_taille),
      [ParkingTypes.Rack]: parse(rack_double_etage) + parse(ratelier)
    };
    const furniture = {
      [ParkingFurnitureTypes.Arch]: parse(arceau),
      [ParkingFurnitureTypes.BigArch]: parse(arceau_velo_grande_taille),
      [ParkingFurnitureTypes.DoubleDeckerRack]: parse(rack_double_etage),
      [ParkingFurnitureTypes.Rack]: parse(ratelier),
      [ParkingFurnitureTypes.HandlebarSupport]: parse(support_guidon || null),
      [ParkingFurnitureTypes.Hook]: parse(crochet || null),
      [ParkingFurnitureTypes.Bollard]: parse(potelet || null),
      [ParkingFurnitureTypes.NoEquipment]: parse(aucun_equipement || null),
      [ParkingFurnitureTypes.Other]: parse(autre || null)
    };
    distancesList.push(new CyclabilityZoneStats(moment(created), {
      ...distances,
      all: Object.values(distances).reduce((res, distance) => res + distance, 0)
    }));
    slotsList.push(new CyclabilityZoneBNSCStats(moment(created), {
      ...slots,
      all: parse(capacite)
    }, furniture));
  });
  return new CyclabilityZone(id || -1, administrativeLevels[administrative_level], reference || null, country_code?.toLowerCase() || null, name || '', geometry || geometrySimplified || {
    type: 'MultiPolygon',
    coordinates: []
  }, distancesList, slotsList);
}